import { useNavigate, useParams } from "@tanstack/react-router"

import { Avatar, AvatarFallback, AvatarImage, Card, Progress } from "@atlant/ui"

import type { FleetListQuery } from "#gql/graphql.ts"
import AircraftImage from "#images/aircraft-image.png"
import { FleetRowActions } from "#pages/authenticated/fleet/components/table/table-fleet-row-action.tsx"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { getFileUrl } from "#utils/file-url.ts"

interface AircraftCardProps {
  aircraft: FleetListQuery["aircraftByOrganization"][number]
}

export const AircraftCard = ({ aircraft }: AircraftCardProps) => {
  const navigate = useNavigate()
  const { slug } = useParams({ from: "/_authenticated/$slug" })
  const { me } = useLoggedInUserInfo()
  const lastMonthCost = "€105,300"
  const lastMonthChange = "↓ 2.6% vs Apr"
  const ytdHoursValue = "215.4"
  const ytdHoursChange = "↑ 18.7 vs LY"
  const docTrendValue = "+2.3% ↑"
  const docTrendSub = "vs LY"
  const budgetPercentage = 68
  const budgetText = "€420K / €615K"
  const flightStatus = "In flight"

  const primaryImage = aircraft.images?.length
    ? getFileUrl(me.organization.id, "aircraft", aircraft.images[0])
    : AircraftImage
  const manufacturerModel = [aircraft.manufacturer, aircraft.model]
    .filter(Boolean)
    .join(" ")
  const ownerName = aircraft.owners?.[0]?.name ?? "—"
  const kams = aircraft.keyAccountManagers

  const handleClick = () => {
    void navigate({
      to: "/$slug/fleet/$aircraftId/overview",
      params: { slug, aircraftId: aircraft.id },
    })
  }

  return (
    <Card
      onClick={handleClick}
      className="border-px bg-card text-card-foreground w-full cursor-pointer overflow-hidden rounded-xl p-4 shadow-sm transition-all"
    >
      <div className="flex flex-col items-start justify-between gap-5 sm:flex-row">
        <div className="flex w-full min-w-0 items-start gap-4 sm:flex-1">
          <img
            src={primaryImage}
            alt={aircraft.tail}
            className="h-25 w-35 shrink-0 rounded-sm object-contain"
          />
          <div className="flex min-w-0 flex-col">
            <div className="flex flex-wrap items-center gap-x-3">
              <h3 className="text-xl font-bold tracking-tight">{aircraft.tail}</h3>
              <span className="text-muted-foreground text-sm font-medium">
                {manufacturerModel}
              </span>
            </div>
            <p className="text-muted-foreground mt-0.5 text-sm">
              {aircraft.serial ? `S/N: ${aircraft.serial}` : ""}{" "}
              {aircraft.yom && <span className="text-separator">•</span>}{" "}
              {aircraft.yom ? aircraft.yom : ""}
            </p>

            <div className="mt-4 flex items-center gap-16">
              <div className="flex min-w-0 items-center gap-2">
                {kams.length > 0 && (
                  <div className="flex shrink-0 -space-x-2">
                    {kams.map((k) => (
                      <Avatar key={k.id} className="border-background size-7 border-2">
                        <AvatarImage src={k.image ?? undefined} alt={k.name} />
                        <AvatarFallback className="bg-avatar text-xs font-medium text-white">
                          {k.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                  </div>
                )}
                <div className="flex min-w-0 flex-col text-xs">
                  <span className="text-muted-foreground">KAM</span>
                  <span className="text-foreground truncate font-medium">
                    {kams.length > 0 ? kams.map((k) => k.name).join(", ") : "—"}
                  </span>
                </div>
              </div>

              <div className="flex flex-col text-xs">
                <span className="text-muted-foreground">Owner</span>
                <span className="text-foreground font-medium">{ownerName}</span>
              </div>
            </div>
          </div>
        </div>

        <div
          className="flex shrink-0 items-center gap-3 self-end sm:self-start"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="inline-flex items-center gap-1.5 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-medium whitespace-nowrap text-indigo-600 dark:border-indigo-900 dark:bg-indigo-950/50 dark:text-indigo-400">
            <span className="size-1.5 animate-pulse rounded-full bg-red-500" />
            {flightStatus}
          </div>
          <FleetRowActions
            aircraftId={aircraft.id}
            source={aircraft.source}
            currentKamIds={aircraft.keyAccountManagers.map((k) => k.id)}
            currentOwnerId={aircraft.owners?.[0]?.id}
          />
        </div>
      </div>

      <div className="border-t" />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="flex flex-col">
          <span className="text-muted-foreground text-xs">Last month</span>
          <span className="mt-0.5 text-sm font-semibold">{lastMonthCost}</span>
          <span className="mt-0.5 flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400">
            {lastMonthChange}
          </span>
        </div>

        <div className="flex flex-col">
          <span className="text-muted-foreground text-xs">YTD hours</span>
          <span className="mt-0.5 text-sm font-semibold">{ytdHoursValue}</span>
          <span className="mt-0.5 flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400">
            {ytdHoursChange}
          </span>
        </div>

        <div className="flex flex-col">
          <span className="text-muted-foreground text-xs">DOC trend</span>
          <span className="mt-0.5 text-sm font-semibold text-rose-600 dark:text-rose-400">
            {docTrendValue}
          </span>
          <span className="text-muted-foreground mt-0.5 text-sm">{docTrendSub}</span>
        </div>

        <div className="flex flex-col">
          <span className="text-muted-foreground text-xs">Budget usage</span>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className="text-sm font-semibold">{budgetPercentage}%</span>
          </div>
          <div className="mt-2 flex flex-col gap-1">
            <Progress value={budgetPercentage} className="bg-muted h-2" />
            <span className="text-muted-foreground text-sm">{budgetText}</span>
          </div>
        </div>
      </div>
    </Card>
  )
}
